import { useEffect, useRef, useState } from "react";
import type { Channel, SoundMode } from "@gooner/shared";
import {
  IconBack,
  IconMute,
  IconNext,
  IconPause,
  IconPlay,
  IconVolume,
  IconVolumeOne,
  IconVolumeRadio,
} from "../control/ui/icons";
import { cx } from "../control/ui/primitives";
import { trackAudioMode, trackChannelVolume, trackMasterVolume } from "../lib/channels";

/** What each mode is called and what it does. The glyphs match the bar button's, so the
 *  thing you pointed at and the thing you are choosing agree. */
const MODES: ReadonlyArray<{
  mode: SoundMode;
  label: string;
  desc: string;
  Icon: typeof IconVolume;
}> = [
  { mode: "muted", label: "Muted", desc: "Nothing plays.", Icon: IconMute },
  { mode: "radio", label: "Radio", desc: "The channel only — every panel stays silent.", Icon: IconVolumeRadio },
  { mode: "one", label: "Play 1", desc: "One panel at a time — the channel fills its silences.", Icon: IconVolumeOne },
  { mode: "all", label: "Play all", desc: "Every panel the layout gave sound to.", Icon: IconVolume },
];

/**
 * The master audio control: this screen's sound mode, its master fader, and (next) the
 * radio channels, hanging off the bar's sound button.
 *
 * RENDERED BESIDE ITS BUTTON, not portaled — and that is an accessibility decision, not a
 * styling one. Portaled to document.body this panel worked perfectly with a mouse and was
 * unreachable without one: it never opened on focus, and once a mouse had opened it its
 * contents sat at the END of the document's tab order, six stops past the control that
 * owns them. Since the four modes and the master fader exist ONLY here, that made them
 * keyboard-inaccessible outright. As a sibling of the button it is next in tab order, and
 * focus entering the group is one more way to open it.
 *
 * `absolute` rather than `fixed` is what makes that possible inside the bar, whose
 * `backdrop-blur` is a containing block for fixed descendants — the trap the account modal
 * and the region link menu portal to escape. Absolute positioning is unaffected by it, and
 * the bar's own menu has hung this way all along. It also drops a measured rect that went
 * stale on any resize.
 *
 * DISMISSAL IS THIS COMPONENT'S JOB, so ScreenControls keeps its rule of owning no state.
 * The outside test asks the PARENT rather than this element: the button is that parent's
 * other child, and without excluding it a pointer-down on the button would close the panel
 * and the click that follows would reopen it, which reads as a dead button.
 *
 * It does NOT own any dialog it can open. A dropdown that dismisses on outside
 * pointer-down unmounts a modal it owns on that modal's first click — the failure the
 * account menu documents — so anything richer than this is reported upward and mounted by
 * the screen, above this panel's lifetime.
 */
export function MasterAudioPanel({
  mode,
  onMode,
  master,
  onMaster,
  soloed,
  eligibleCount,
  channels,
  current,
  playing,
  radioLevel,
  onRadioLevel,
  onChannel,
  onPlayPause,
  onPartner,
  onClose,
}: {
  mode: SoundMode;
  onMode: (mode: SoundMode) => void;
  master: number;
  onMaster: (level: number) => void;
  /** Whether Play 1 currently has a panel to sound. */
  soloed: boolean;
  /** How many panels the layout has left audible — Play 1's pool. */
  eligibleCount: number;
  /** The curated channel list. An EMPTY list still renders the row, greyed and inert —
   *  the area is not mode-gated and does not come and go, so the panel keeps its height
   *  the day a partner signs up. */
  channels: readonly Channel[];
  current: Channel | null;
  playing: boolean;
  /** The channel's OWN level, under the transport — junior to the master, which still
   *  multiplies it. What lets the radio sit quietly beneath the clips (or over them)
   *  without touching what the whole screen plays at. */
  radioLevel: number;
  onRadioLevel: (level: number) => void;
  /** Tune to a channel — `dir` walks the list, wrapping. */
  onChannel: (dir: 1 | -1) => void;
  onPlayPause: () => void;
  /** The track body was clicked. Reported UPWARD rather than opening the dialog here: a
   *  dropdown that dismisses on outside pointer-down would unmount a modal it owned on
   *  that modal's first click. The screen owns the dialog. */
  onPartner: (channel: Channel) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // The group, not this element: the button that opens this panel is our sibling, and
      // a pointer-down on it must not count as "outside".
      if (ref.current?.parentElement?.contains(e.target as Node)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  /** The mode being pointed at or focused, which the description line answers. Null means
   *  nobody is asking, and the line falls back to the mode actually in force. */
  const [preview, setPreview] = useState<SoundMode | null>(null);

  const dragging = useRef(false);
  const endDrag = () => {
    if (!dragging.current) return;
    dragging.current = false;
    trackMasterVolume(master);
  };

  const warning =
    mode === "radio" && channels.length === 0
      ? "No channels yet — this silences the panels and plays nothing."
      : mode === "one" && eligibleCount === 0
        ? "No panel on this wall has sound turned on — turn one on from its hover controls."
        : mode === "one" && !soloed
          ? playing
            ? "No panel is speaking — the channel is filling in."
            : "Waiting for a panel with audio."
          : null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Screen audio"
      className="absolute right-2 top-full z-50 mt-1 w-80 sm:right-0 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] text-left shadow-2xl"
    >
      {/*
        THE MODES, as one row of four rather than four stacked rows — which is what makes
        room for a channel underneath without the panel becoming a page.

        Each button carries its own title, and the DESCRIPTION moves to a shared line
        below: four descriptions stacked would be four lines of text nobody reads, while
        one line that answers whatever you are pointing at gets read every time.
      */}
      <div className="flex gap-0.5 p-1.5">
        {MODES.map(({ mode: m, label, Icon }) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              trackAudioMode(m);
              onMode(m);
            }}
            aria-pressed={mode === m}
            // FOCUS AS WELL AS HOVER. This panel is keyboard-reachable, and a description
            // that only answered a pointer would be invisible to exactly the people who
            // most need the label spelled out. Touch gets neither, which is why the line
            // falls back to the ACTIVE mode's description rather than going blank.
            onPointerEnter={() => setPreview(m)}
            onPointerLeave={() => setPreview((p) => (p === m ? null : p))}
            onFocus={() => setPreview(m)}
            onBlur={() => setPreview((p) => (p === m ? null : p))}
            className={cx(
              "flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors",
              mode === m
                ? "bg-[var(--color-accent)]/15 text-[var(--color-accent-soft)]"
                : "text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-zinc-200",
            )}
          >
            <Icon width={16} height={16} />
            <span className="text-[10px] leading-none">{label}</span>
          </button>
        ))}
      </div>

      {/* Whatever you are pointing at, else the mode you are actually in. `min-h` so a
          two-line description cannot make the panel jump under the pointer. */}
      <p className="min-h-[2.25rem] border-t border-[var(--color-edge)] px-3 py-2 text-[11px] leading-snug text-zinc-400">
        {(MODES.find((r) => r.mode === (preview ?? mode)) ?? MODES[0]).desc}
      </p>

      {/*
        A mode that silently does nothing is the invisible failure this repo writes checks
        about, so the two that can be silent say so. Keyed off the ACTIVE mode, never the
        previewed one — pointing at Play 1 must not raise an alarm about a wall you have
        not switched to. Its own line, so the description and the warning are both readable
        at once rather than fighting for the same row.
      */}
      {warning ? (
        <p className="border-t border-[var(--color-edge)] bg-[var(--color-accent)]/5 px-3 py-2 text-[11px] leading-snug text-[var(--color-accent-soft)]/80">
          {warning}
        </p>
      ) : null}

      <div className="border-t border-[var(--color-edge)] px-3 py-2.5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-zinc-500">Master</span>
          <span className="text-[11px] tabular-nums text-zinc-400">
            {Math.round(master * 100)}
          </span>
        </div>
        {/* `touch-none` because a horizontal drag on a range input inside a touch surface
            is otherwise ambiguous with a pan, and the wall is a touch surface. */}
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(master * 100)}
          aria-label="Master volume"
          title="Master volume for this screen"
          className="h-1 w-full cursor-pointer touch-none accent-[var(--color-accent)]"
          onChange={(e) => onMaster(Number(e.currentTarget.value) / 100)}
          // One event per DRAG, at its settle — the region slider's rule. pointerup and
          // lostpointercapture both end a drag and often both fire, so the flag makes them
          // one report carrying the level the hand actually left it at; and the second is
          // the net under the first, because a range input captures the pointer and a drag
          // released off-window sends no pointerup here.
          onPointerDown={() => {
            dragging.current = true;
          }}
          onPointerUp={endDrag}
          onLostPointerCapture={endDrag}
        />
      </div>

      <ChannelRow
        channel={current}
        playing={playing}
        many={channels.length > 1}
        level={radioLevel}
        onLevel={onRadioLevel}
        onPrev={() => onChannel(-1)}
        onNext={() => onChannel(1)}
        onPlayPause={onPlayPause}
        onOpenPartner={() => current && onPartner(current)}
      />
    </div>
  );
}

/**
 * The tuned channel: its artwork, what it is, and the transport.
 *
 * ONE SHELL, SIBLING BUTTONS. The row is a `div`, the track body is a `button`, and the
 * transport buttons sit beside it rather than inside it — HTML forbids nesting buttons,
 * and the bar's own code chip is built the same way for the same reason. It also means
 * the transport does not have to `stopPropagation` its way out of an enclosing click.
 *
 * The track body opens the partner's credit. That is the trade the channels exist for:
 * a partner supplies the music, and the row is where they are credited — on a deliberate
 * press, never on a timer, which is what keeps it a credit rather than an ad on a screen
 * that carries none.
 *
 * The artwork blurs under safe mode, like every other img. The name, the artist and the
 * "who made this" affordance are text and survive it.
 *
 * IT RENDERS WITH NO CHANNEL TOO, greyed and inert. The row is not mode-gated and does not
 * come and go: an area that appears the day a partner signs up would move everything above
 * it, and an empty shelf that simply vanished would leave the panel silently shorter with
 * nothing to say why. Not `aria-hidden` — "no channels yet" is real information about the
 * feature's state, not decoration.
 */
function ChannelRow({
  channel,
  playing,
  many,
  level,
  onLevel,
  onPrev,
  onNext,
  onPlayPause,
  onOpenPartner,
}: {
  channel: Channel | null;
  playing: boolean;
  /** Whether stepping between channels is meaningful — one channel has nowhere to step. */
  many: boolean;
  /** The channel's own fader — see MasterAudioPanel's radioLevel. */
  level: number;
  onLevel: (level: number) => void;
  onPrev: () => void;
  onNext: () => void;
  onPlayPause: () => void;
  onOpenPartner: () => void;
}) {
  const step =
    "rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-100 disabled:opacity-30 disabled:hover:bg-transparent";
  // One event per DRAG, at its settle — the master fader's rule, for the same reasons.
  const dragging = useRef(false);
  const endDrag = () => {
    if (!dragging.current) return;
    dragging.current = false;
    trackChannelVolume(level);
  };
  return (
    <div className="border-t border-[var(--color-edge)] px-2.5 py-2.5">
      <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onOpenPartner}
        disabled={!channel}
        title={channel ? `About ${channel.partner.name}` : undefined}
        className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 text-left transition-colors enabled:hover:bg-[var(--color-panel-2)] disabled:cursor-default"
      >
        {channel ? (
          <img
            src={channel.artUrl}
            alt=""
            draggable={false}
            className="size-9 shrink-0 rounded-md object-cover"
          />
        ) : (
          <span className="size-9 shrink-0 rounded-md bg-[var(--color-panel-2)]" />
        )}
        <span className="min-w-0 flex-1">
          <span
            className={cx(
              "block truncate text-sm leading-tight",
              channel ? "text-zinc-100" : "text-zinc-500",
            )}
          >
            {channel ? channel.name : "No channels yet"}
          </span>
          <span className="block truncate text-[11px] leading-tight text-zinc-500">
            {channel ? channel.artist : "Coming soon"}
          </span>
        </span>
      </button>

      <span className="flex shrink-0 items-center">
        <button
          type="button"
          onClick={onPrev}
          disabled={!channel || !many}
          aria-label="Previous channel"
          className={step}
        >
          <IconBack width={14} height={14} />
        </button>
        <button
          type="button"
          onClick={onPlayPause}
          disabled={!channel}
          aria-label={playing ? "Pause channel" : "Play channel"}
          className="rounded-full bg-white/10 p-2 text-zinc-100 transition-colors enabled:hover:bg-white/20 disabled:opacity-30"
        >
          {playing ? <IconPause width={14} height={14} /> : <IconPlay width={14} height={14} />}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!channel || !many}
          aria-label="Next channel"
          className={step}
        >
          <IconNext width={14} height={14} />
        </button>
      </span>
      </div>

      {/* THE CHANNEL'S OWN FADER, under the transport and deliberately smaller than the
          master's: it is junior (the master still multiplies it), and drawing it at the
          master's weight would read as a second master. Greyed with the transport when
          there is no channel, for the same keep-the-shelf reason as the row above. */}
      <div className="mt-2 flex items-center gap-2 px-1">
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(level * 100)}
          disabled={!channel}
          aria-label="Channel volume"
          title="This channel's volume — the master still scales it"
          className="h-0.5 flex-1 cursor-pointer touch-none accent-[var(--color-accent)] disabled:cursor-default disabled:opacity-30"
          onChange={(e) => onLevel(Number(e.currentTarget.value) / 100)}
          onPointerDown={() => {
            dragging.current = true;
          }}
          onPointerUp={endDrag}
          onLostPointerCapture={endDrag}
        />
        <span
          className={cx(
            "w-6 text-right text-[10px] tabular-nums",
            channel ? "text-zinc-500" : "text-zinc-600",
          )}
        >
          {Math.round(level * 100)}
        </span>
      </div>
    </div>
  );
}
